import { useState, useEffect, useCallback, useMemo } from 'react';
import { WorkOrderWithBuilding, WorkOrderStatus } from '../../types/workOrders';
import { workOrdersApi } from '../../api/workOrdersApi';

interface UseProjectDelmomentResult {
    /**
     * Linked, non-cancelled work orders per project. Projects with no delmoment
     * are absent. Order is unspecified; sort with sortDelmoment() when listing.
     */
    byProject: Map<string, WorkOrderWithBuilding[]>;
    loading: boolean;
    error: string | null;
    refresh: () => Promise<void>;
}

/**
 * All delmoment (work orders with a `projectKey`) across every project, read
 * once from the active and archive nodes and grouped by project. One fetch
 * serves the whole project board; the detail page keeps its own per-project
 * hook (useProjectWorkOrders).
 */
export function useProjectDelmoment(): UseProjectDelmomentResult {
    const [workOrders, setWorkOrders] = useState<WorkOrderWithBuilding[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [active, archive] = await Promise.all([
                workOrdersApi.getAll(),
                workOrdersApi.getArchive()
            ]);
            setWorkOrders([...active, ...archive].filter(wo => !!wo.projectKey));
        } catch (err) {
            console.error('Error loading delmoment:', err);
            setError('Kunde inte ladda delmoment');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const byProject = useMemo(() => {
        const map = new Map<string, WorkOrderWithBuilding[]>();
        workOrders.forEach(wo => {
            // Cancelled work orders are not part of the project's scope.
            if (wo.status === WorkOrderStatus.CANCELLED || !wo.projectKey) return;
            const list = map.get(wo.projectKey);
            if (list) {
                list.push(wo);
            } else {
                map.set(wo.projectKey, [wo]);
            }
        });
        return map;
    }, [workOrders]);

    return { byProject, loading, error, refresh: load };
}
